import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type ArtifactType = 'note' | 'notes-list' | 'file' | 'image' | 'code' | 'browser';

export interface ArtifactTab {
  id: string;
  type: ArtifactType;
  title: string;
  data?: unknown;
}

export interface Artifact {
  id: string;
  type: ArtifactType;
  title: string;
  icon?: string;
  isPinned: boolean;
  lastOpened: number;
  createdAt: number;
  modifiedAt: number;
  data?: unknown;
  metadata?: {
    size?: number;
    unsaved?: boolean;
    url?: string; // for browser tabs
    viewMode?: 'edit' | 'preview'; // for notes
  };
}

export interface SectionState {
  collapsed: boolean;
  sortBy: 'modified' | 'created' | 'name';
}

interface ArtifactPanelStore {
  // Panel state
  isOpen: boolean;
  sidebarCollapsed: boolean;
  sidebarWidth: number;
  splitRatio: number; // 0.5 = 50/50 split
  layoutOrientation: 'left' | 'right'; // Position of artifacts panel

  // Organization
  pinnedIds: string[];
  recentIds: string[]; // Last 8 opened

  // Section states
  sections: {
    pinned: { collapsed: boolean };
    recent: { collapsed: boolean };
    notes: SectionState;
    browser: SectionState;
    files: SectionState;
    images: SectionState;
    code: SectionState;
  };

  // Tabs
  openTabs: string[]; // Array of artifact IDs
  activeTabId: string | null;

  // Artifacts registry
  artifacts: Record<string, Artifact>;

  // UI state
  searchQuery: string;

  // Panel actions
  openPanel: () => void;
  closePanel: () => void;
  togglePanel: () => void;
  setSplitRatio: (ratio: number) => void;
  toggleSidebar: () => void;
  setSidebarWidth: (width: number) => void;
  setLayoutOrientation: (orientation: 'left' | 'right') => void;
  toggleLayoutOrientation: () => void;

  // Tab management
  openTab: (tab: ArtifactTab) => void;
  closeTab: (tabId: string) => void;
  setActiveTab: (tabId: string) => void;
  updateTab: (tabId: string, updates: Partial<ArtifactTab>) => void;
  closeAllTabs: () => void;
  closeOtherTabs: (tabId: string) => void;
  reopenLastClosedTab: () => void;

  // Artifact management
  registerArtifact: (artifact: Artifact) => void;
  updateArtifact: (id: string, updates: Partial<Artifact>) => void;
  deleteArtifact: (id: string) => void;
  getArtifact: (id: string) => Artifact | undefined;
  getArtifactsByType: (type: ArtifactType) => Artifact[];

  // Organization actions
  togglePin: (artifactId: string) => void;
  pinArtifact: (artifactId: string) => void;
  unpinArtifact: (artifactId: string) => void;
  addToRecent: (artifactId: string) => void;

  // Section actions
  toggleSection: (section: keyof ArtifactPanelStore['sections']) => void;
  setSectionSort: (section: 'notes' | 'browser' | 'files' | 'images' | 'code', sortBy: SectionState['sortBy']) => void;

  // Search
  setSearchQuery: (query: string) => void;

  // Convenience methods (backward compatible)
  openNote: (noteId: string, title: string) => void;
  openNotesList: () => void;
  openFile: (fileId: string, title: string, data?: unknown) => void;
  openImage: (imageId: string, title: string, data?: unknown) => void;
  openBrowser: (url: string, title?: string) => void;
  createNote: (title?: string, content?: string) => void;
}

export const useArtifactPanelStore = create<ArtifactPanelStore>()(
  persist(
    (set, get) => ({
      // Initial state
      isOpen: false,
      sidebarCollapsed: false,
      sidebarWidth: 260,
      splitRatio: 0.5,
      layoutOrientation: 'right',
      pinnedIds: [],
      recentIds: [],
      sections: {
        pinned: { collapsed: false },
        recent: { collapsed: false },
        notes: { collapsed: false, sortBy: 'modified' },
        browser: { collapsed: false, sortBy: 'modified' },
        files: { collapsed: false, sortBy: 'modified' },
        images: { collapsed: false, sortBy: 'modified' },
        code: { collapsed: false, sortBy: 'modified' },
      },
      openTabs: [],
      activeTabId: null,
      artifacts: {},
      searchQuery: '',

      // Panel actions
      openPanel: () => set({ isOpen: true }),
      closePanel: () => set({ isOpen: false }),
      togglePanel: () => set((state) => ({ isOpen: !state.isOpen })),

      setSplitRatio: (ratio) => {
        const clampedRatio = Math.max(0.2, Math.min(0.8, ratio));
        set({ splitRatio: clampedRatio });
      },

      toggleSidebar: () => set((state) => ({ sidebarCollapsed: !state.sidebarCollapsed })),

      setSidebarWidth: (width) => {
        const clampedWidth = Math.max(200, Math.min(400, width));
        set({ sidebarWidth: clampedWidth });
      },

      setLayoutOrientation: (orientation) => {
        set({ layoutOrientation: orientation });
      },

      toggleLayoutOrientation: () => {
        set((state) => ({
          layoutOrientation: state.layoutOrientation === 'left' ? 'right' : 'left',
        }));
      },

      // Tab management
      openTab: (tab) => {
        const { openTabs, artifacts } = get();

        // Register artifact if not exists
        if (!artifacts[tab.id]) {
          get().registerArtifact({
            id: tab.id,
            type: tab.type,
            title: tab.title,
            isPinned: false,
            lastOpened: Date.now(),
            createdAt: Date.now(),
            modifiedAt: Date.now(),
            data: tab.data,
          });
        }

        // Add to recent
        get().addToRecent(tab.id);

        // Update last opened
        get().updateArtifact(tab.id, { lastOpened: Date.now() });

        if (openTabs.includes(tab.id)) {
          // Tab already open, just activate it
          set({ activeTabId: tab.id, isOpen: true });
        } else {
          // Add new tab and activate it
          set({
            openTabs: [...openTabs, tab.id],
            activeTabId: tab.id,
            isOpen: true,
          });
        }
      },

      closeTab: (tabId) => {
        const { openTabs, activeTabId } = get();
        const newOpenTabs = openTabs.filter((id) => id !== tabId);

        let newActiveTabId = activeTabId;
        if (activeTabId === tabId) {
          const closedIndex = openTabs.findIndex((id) => id === tabId);
          if (newOpenTabs.length > 0) {
            const newIndex = Math.min(closedIndex, newOpenTabs.length - 1);
            newActiveTabId = newOpenTabs[newIndex] ?? null;
          } else {
            newActiveTabId = null;
          }
        }

        set({
          openTabs: newOpenTabs,
          activeTabId: newActiveTabId,
          isOpen: newOpenTabs.length > 0,
        });
      },

      setActiveTab: (tabId) => {
        const { openTabs } = get();
        if (openTabs.includes(tabId)) {
          set({ activeTabId: tabId });
          get().addToRecent(tabId);
          get().updateArtifact(tabId, { lastOpened: Date.now() });
        }
      },

      updateTab: (tabId, updates) => {
        get().updateArtifact(tabId, {
          title: updates.title,
          modifiedAt: Date.now(),
        });
      },

      closeAllTabs: () => {
        set({ openTabs: [], activeTabId: null, isOpen: false });
      },

      closeOtherTabs: (tabId) => {
        set({ openTabs: [tabId], activeTabId: tabId });
      },

      reopenLastClosedTab: () => {
        // TODO: Implement reopen closed tab history
      },

      // Artifact management
      registerArtifact: (artifact) => {
        set((state) => ({
          artifacts: {
            ...state.artifacts,
            [artifact.id]: artifact,
          },
        }));
      },

      updateArtifact: (id, updates) => {
        set((state) => {
          const artifact = state.artifacts[id];
          if (!artifact) return state;

          return {
            artifacts: {
              ...state.artifacts,
              [id]: { ...artifact, ...updates },
            },
          };
        });
      },

      deleteArtifact: (id) => {
        const { artifacts, openTabs, pinnedIds, recentIds } = get();
        const newArtifacts = { ...artifacts };
        delete newArtifacts[id];

        // Close tab if open
        if (openTabs.includes(id)) {
          get().closeTab(id);
        }

        set({
          artifacts: newArtifacts,
          pinnedIds: pinnedIds.filter((pid) => pid !== id),
          recentIds: recentIds.filter((rid) => rid !== id),
        });
      },

      getArtifact: (id) => {
        return get().artifacts[id];
      },

      getArtifactsByType: (type) => {
        const { artifacts } = get();
        return Object.values(artifacts).filter((a) => a.type === type);
      },

      // Organization actions
      togglePin: (artifactId) => {
        const { artifacts, pinnedIds } = get();
        const artifact = artifacts[artifactId];
        if (!artifact) return;

        if (pinnedIds.includes(artifactId)) {
          get().unpinArtifact(artifactId);
        } else {
          get().pinArtifact(artifactId);
        }
      },

      pinArtifact: (artifactId) => {
        const { pinnedIds } = get();
        if (!pinnedIds.includes(artifactId)) {
          set({ pinnedIds: [...pinnedIds, artifactId] });
          get().updateArtifact(artifactId, { isPinned: true });
        }
      },

      unpinArtifact: (artifactId) => {
        set((state) => ({
          pinnedIds: state.pinnedIds.filter((id) => id !== artifactId),
        }));
        get().updateArtifact(artifactId, { isPinned: false });
      },

      addToRecent: (artifactId) => {
        set((state) => {
          const filtered = state.recentIds.filter((id) => id !== artifactId);
          const newRecent = [artifactId, ...filtered].slice(0, 8);
          return { recentIds: newRecent };
        });
      },

      // Section actions
      toggleSection: (section) => {
        set((state) => ({
          sections: {
            ...state.sections,
            [section]: {
              ...state.sections[section],
              collapsed: !state.sections[section].collapsed,
            },
          },
        }));
      },

      setSectionSort: (section, sortBy) => {
        set((state) => ({
          sections: {
            ...state.sections,
            [section]: {
              ...state.sections[section],
              sortBy,
            },
          },
        }));
      },

      // Search
      setSearchQuery: (query) => set({ searchQuery: query }),

      // Convenience methods (backward compatible)
      openNote: (noteId, title) => {
        get().openTab({
          id: `note-${noteId}`,
          type: 'note',
          title,
          data: { noteId },
        });
      },

      openNotesList: () => {
        get().openTab({
          id: 'notes-list',
          type: 'notes-list',
          title: 'Notes',
        });
      },

      openFile: (fileId, title, data) => {
        get().openTab({
          id: `file-${fileId}`,
          type: 'file',
          title,
          data: { fileId, ...(data as Record<string, unknown> ?? {}) },
        });
      },

      openImage: (imageId, title, data) => {
        get().openTab({
          id: `image-${imageId}`,
          type: 'image',
          title,
          data: { imageId, ...(data as Record<string, unknown> ?? {}) },
        });
      },

      openBrowser: (url, title) => {
        const urlId = url ? btoa(url).slice(0, 12) : Date.now().toString();
        const artifactId = `browser-${urlId}`;
        let displayTitle = title || 'Browser';

        if (url) {
          try {
            displayTitle = title || new URL(url).hostname;
          } catch {
            displayTitle = title || 'Browser';
          }
        }

        get().openTab({
          id: artifactId,
          type: 'browser',
          title: displayTitle,
          data: { url },
        });

        // Update artifact metadata with URL
        get().updateArtifact(artifactId, {
          metadata: { url },
        });
      },

      createNote: (title, content) => {
        const noteId = `note-${Date.now()}`;
        const noteTitle = title || 'Untitled Note';

        get().openTab({
          id: noteId,
          type: 'note',
          title: noteTitle,
          data: {
            noteId,
            initialTitle: noteTitle,
            initialContent: content || '',
            isNew: true,
          },
        });
      },
    }),
    {
      name: 'artifact-panel-storage',
      partialize: (state) => ({
        sidebarCollapsed: state.sidebarCollapsed,
        sidebarWidth: state.sidebarWidth,
        splitRatio: state.splitRatio,
        layoutOrientation: state.layoutOrientation,
        pinnedIds: state.pinnedIds,
        sections: state.sections,
        artifacts: state.artifacts,
        openTabs: state.openTabs,
        activeTabId: state.activeTabId,
      }),
    }
  )
);
